import { useMemo } from "react";
import { useExtracted } from "next-intl";
import type { OutputField, SweepField } from "@/lib/manifold/axes";
import type { NamingLabels } from "@/lib/strategy/naming";
import type { StrategyLayer, StrategyLayerKind } from "@/types/strategy";

export function useKindLabels(): Record<string, string> {
  const te = useExtracted();
  return useMemo(
    () => ({
      single_call: te("Call"),
      single_put: te("Put"),
      covered_call: te("Covered Call"),
      cash_secured_put: te("Cash Secured Put"),
      bull_call_spread: te("Bull Call Spread"),
      bear_put_spread: te("Bear Put Spread"),
      bull_put_spread: te("Bull Put Spread"),
      bear_call_spread: te("Bear Call Spread"),
      iron_condor: te("Iron Condor"),
      iron_fly: te("Iron Fly"),
      butterfly: te("Butterfly"),
      broken_wing_butterfly: te("Broken Wing Butterfly"),
      long_condor: te("Long Condor"),
      short_condor: te("Short Condor"),
      jade_lizard: te("Jade Lizard"),
      ratio_backspread: te("Ratio Backspread"),
      ratio_spread: te("Ratio Spread"),
      bull_call_ladder: te("Bull Call Ladder"),
      bear_call_ladder: te("Bear Call Ladder"),
      bull_put_ladder: te("Bull Put Ladder"),
      bear_put_ladder: te("Bear Put Ladder"),
      synthetic_future: te("Synthetic Future"),
      combo: te("Combo"),
      strip: te("Strip"),
      strap: te("Strap"),
      guts: te("Guts"),
      time_spread: te("Time Spread"),
      double_time_spread: te("Double Time Spread"),
      straddle: te("Straddle"),
      strangle: te("Strangle"),
      custom: te("Custom"),
    }),
    [te],
  );
}

export function useNamingLabels(): NamingLabels {
  const te = useExtracted();
  return {
    kinds: useKindLabels(),
    long: te("Long"),
    short: te("Short"),
    upper: te("Upper"),
    lower: te("Lower"),
    reverse: te("Reverse"),
    call: te("Call"),
    put: te("Put"),
    newStrategy: te("New Strategy"),
    newTab: te("New Tab"),
  };
}

export function useCategoryLabels(): Record<string, string> {
  const te = useExtracted();
  return useMemo(
    () => ({
      single: te("Single Leg"),
      covered: te("Covered"),
      vertical: te("Vertical Spreads"),
      multi_leg: te("Multi-Leg"),
      income: te("Income"),
      ratio: te("Ratio / Ladder"),
      synthetic: te("Synthetic"),
      time: te("Time-Based"),
      volatility: te("Volatility"),
      custom: te("Custom"),
    }),
    [te],
  );
}

export type KindSummary = { profit: string; risk: string };

export function useKindSummaries(): Record<
  Exclude<StrategyLayerKind, "custom">,
  KindSummary
> {
  const te = useExtracted();
  return {
    single_call: {
      profit: te("theoretically unlimited upside"),
      risk: te("premium risk"),
    },
    single_put: {
      profit: te("theoretically large limited upside"),
      risk: te("premium risk"),
    },
    covered_call: {
      profit: te("theoretically capped above short call"),
      risk: te("stock downside"),
    },
    cash_secured_put: {
      profit: te("theoretically premium capped"),
      risk: te("assigned-stock downside"),
    },
    bull_call_spread: {
      profit: te("width minus net debit"),
      risk: te("net debit"),
    },
    bear_put_spread: {
      profit: te("width minus net debit"),
      risk: te("net debit"),
    },
    bull_put_spread: {
      profit: te("net credit"),
      risk: te("width minus credit"),
    },
    bear_call_spread: {
      profit: te("net credit"),
      risk: te("width minus credit"),
    },
    iron_condor: {
      profit: te("net credit"),
      risk: te("one-side spread width minus credit"),
    },
    iron_fly: {
      profit: te("net credit"),
      risk: te("wing width minus credit"),
    },
    butterfly: {
      profit: te("wing width minus net debit"),
      risk: te("net debit"),
    },
    broken_wing_butterfly: {
      profit: te("short-body profit peak"),
      risk: te("wider-wing tail risk"),
    },
    long_condor: {
      profit: te("middle-body width minus net debit"),
      risk: te("net debit"),
    },
    short_condor: {
      profit: te("net credit"),
      risk: te("wing width minus credit"),
    },
    jade_lizard: {
      profit: te("net credit"),
      risk: te("naked put downside risk"),
    },
    ratio_backspread: {
      profit: te("breakout-direction profit"),
      risk: te("peak loss between short and long legs"),
    },
    ratio_spread: {
      profit: te("profit peak near short legs"),
      risk: te("extra naked-short tail risk"),
    },
    bull_call_ladder: {
      profit: te("profit peak near first short call"),
      risk: te("naked upside call risk"),
    },
    bear_call_ladder: {
      profit: te("keep net credit below short call"),
      risk: te("loss zone between short and long calls"),
    },
    bull_put_ladder: {
      profit: te("keep net credit above short put"),
      risk: te("loss zone between short and long puts"),
    },
    bear_put_ladder: {
      profit: te("profit peak near first short put"),
      risk: te("naked downside put risk"),
    },
    synthetic_future: {
      profit: te("directional linear profit"),
      risk: te("opposite linear loss"),
    },
    combo: {
      profit: te("split-strike directional profit"),
      risk: te("opposite linear loss"),
    },
    strip: {
      profit: te("large-move profit with downside tilt"),
      risk: te("net debit"),
    },
    strap: {
      profit: te("large-move profit with upside tilt"),
      risk: te("net debit"),
    },
    guts: {
      profit: te("large-move profit"),
      risk: te("net debit"),
    },
    time_spread: {
      profit: te("front expires while back keeps value"),
      risk: te("net debit"),
    },
    double_time_spread: {
      profit: te("either target holds back-month value after front expiry"),
      risk: te("net debit"),
    },
    straddle: {
      profit: te("net credit"),
      risk: te("two-sided naked short risk"),
    },
    strangle: {
      profit: te("net credit"),
      risk: te("two-sided naked short risk"),
    },
  };
}

export function useCustomKindHeadline(): string {
  const te = useExtracted();
  return te("Build a custom single-leg structure in any direction");
}

export function useKindFlowLabels(): Record<StrategyLayerKind, string> {
  const te = useExtracted();
  return {
    single_call: te("Default debit"),
    single_put: te("Default debit"),
    covered_call: te("Credit"),
    cash_secured_put: te("Credit"),
    bull_call_spread: te("Debit"),
    bear_put_spread: te("Debit"),
    bull_put_spread: te("Credit"),
    bear_call_spread: te("Credit"),
    iron_condor: te("Default credit"),
    iron_fly: te("Default credit"),
    butterfly: te("Default debit"),
    broken_wing_butterfly: te("Debit/Credit"),
    long_condor: te("Debit"),
    short_condor: te("Credit"),
    jade_lizard: te("Credit"),
    ratio_backspread: te("Usually debit"),
    ratio_spread: te("Usually credit"),
    bull_call_ladder: te("Debit/Credit"),
    bear_call_ladder: te("Debit/Credit"),
    bull_put_ladder: te("Debit/Credit"),
    bear_put_ladder: te("Debit/Credit"),
    synthetic_future: te("Debit/Credit"),
    combo: te("Debit/Credit"),
    strip: te("Debit"),
    strap: te("Debit"),
    guts: te("Default debit"),
    time_spread: te("Usually debit"),
    double_time_spread: te("Usually debit"),
    straddle: te("Default credit"),
    strangle: te("Default credit"),
    custom: te("Custom flow"),
  };
}

export function useKindHeadlineLabels(): Record<
  Exclude<StrategyLayerKind, "custom">,
  string
> {
  const te = useExtracted();
  return {
    single_call: te("Bullish · Profits above breakeven"),
    single_put: te("Bearish · Profits below breakeven"),
    covered_call: te("Long stock · Profits below short call"),
    cash_secured_put: te("Bullish entry · Profits above short put"),
    bull_call_spread: te("Bullish · Profits above upper short leg"),
    bear_put_spread: te("Bearish · Profits below lower short leg"),
    bull_put_spread: te("Mildly bullish · Profits above short put"),
    bear_call_spread: te("Mildly bearish · Profits below short call"),
    iron_condor: te("Range-bound · Profits between short strikes"),
    iron_fly: te("Near ATM · Profits near center strike"),
    butterfly: te("Pinning · Profits near center strike"),
    broken_wing_butterfly: te("Skewed pin · Profits near short body"),
    long_condor: te("Neutral range · Profits inside middle range"),
    short_condor: te("Range breakout · Profits outside middle body"),
    jade_lizard: te(
      "Bullish income · Profits above short put and below call spread",
    ),
    ratio_backspread: te("Breakout · Profits on a large move"),
    ratio_spread: te("Near short legs · Profits near short-leg area"),
    bull_call_ladder: te("Moderate bullish · Profits on a moderate rise"),
    bear_call_ladder: te("Bearish · Profits lower or below short leg"),
    bull_put_ladder: te("Bullish · Profits higher or above short leg"),
    bear_put_ladder: te("Moderate bearish · Profits on a moderate drop"),
    synthetic_future: te("Long/short toggle · Profits when direction is right"),
    combo: te("Long/short toggle · Profits when direction is right"),
    strip: te(
      "Bearish volatility · Profits from a large move, especially down",
    ),
    strap: te("Bullish volatility · Profits from a large move, especially up"),
    guts: te("Volatility · Profits from a large move"),
    time_spread: te(
      "Time decay · Profits when near expiry decays faster near target",
    ),
    double_time_spread: te(
      "Two-sided time decay · Profits near either target zone",
    ),
    straddle: te("Low volatility · Profits near ATM"),
    strangle: te("Wide low volatility · Profits between legs"),
  };
}

export function useKindSideToggleBlocks(): Partial<
  Record<StrategyLayerKind, string>
> {
  const te = useExtracted();
  return {
    single_call: te(
      "**Side toggle**: long (bullish bet, theoretically unlimited upside) or short (collect premium, naked = theoretically unlimited risk).",
    ),
    single_put: te(
      "**Side toggle**: long (bearish bet, large downside profit) or short (collect premium, large risk if assigned).",
    ),
    iron_condor: te(
      "**Side toggle**: flip between short (sell the body, collect credit) and long (buy the body, pay debit).",
    ),
    iron_fly: te(
      "**Side toggle**: flip between short (sell ATM, buy wings) and long (buy ATM, sell wings).",
    ),
    butterfly: te(
      "**Side toggle**: long butterfly (buy wings) or short butterfly (sell wings).",
    ),
    synthetic_future: te(
      "**Side toggle**: long synthetic (bullish) or short synthetic (bearish).",
    ),
    combo: te(
      "**Side toggle**: long combo (buy both) or short combo (sell both).",
    ),
    guts: te(
      "**Side toggle**: long guts (buy both) or short guts (sell both).",
    ),
    straddle: te(
      "**Side toggle**: long straddle (buy both, pay debit) or short straddle (sell both, collect credit).",
    ),
    strangle: te(
      "**Side toggle**: long strangle (buy both) or short strangle (sell both).",
    ),
  };
}

export function useParamLabels(): Record<string, string> {
  const te = useExtracted();
  return {
    width: te("Wing Width"),
    skew: te("Skew"),
    putSkew: te("Put Skew"),
    callSkew: te("Call Skew"),
    direction: te("Direction"),
    s1: te("Short 1 Offset"),
    s2: te("Short 2 Offset"),
    l2: te("Long Wing Offset"),
    putShortOffset: te("Put Short"),
    putLongOffset: te("Put Long"),
    callShortOffset: te("Call Short"),
    callLongOffset: te("Call Long"),
    side: te("Side"),
    callOffset: te("Call Offset"),
    putOffset: te("Put Offset"),
    strikeOffset: te("Strike Offset"),
    longOffset: te("Long Offset"),
    shortOffset: te("Short Offset"),
    optionType: te("Type"),
    frontDTE: te("Front DTE"),
    backDTE: te("Back DTE"),
    innerLow: te("Lower Body"),
    innerHigh: te("Upper Body"),
    outerWidth: te("Wing Width"),
    short1Offset: te("Short 1 Offset"),
    short2Offset: te("Short 2 Offset"),
    long1Offset: te("Long 1 Offset"),
    long2Offset: te("Long 2 Offset"),
    nakedOffset: te("Naked Leg Offset"),
    spreadShortOffset: te("Spread Short Offset"),
    spreadLongOffset: te("Spread Long Offset"),
    bias: te("Bias"),
  };
}

export function useParamHelps(layer?: StrategyLayer): Record<string, string> {
  const te = useExtracted();
  const params = layer?.params ?? {};
  const kind = layer?.kind;
  const isLongSide = (params.side ?? 1) >= 0;
  const isPutType = (params.optionType ?? 0) >= 1;
  const isReverseDirection = (params.direction ?? 1) < 0;
  const longShort = isLongSide ? te("Long") : te("Short");
  const shortLong = isLongSide ? te("Short") : te("Long");
  const optionType = isPutType ? te("Put") : te("Call");
  const direction =
    kind === "jade_lizard"
      ? isReverseDirection
        ? te("Reverse")
        : te("Standard")
      : isReverseDirection
        ? te("Lower")
        : te("Upper");

  const helps: Record<string, string> = {
    strikeOffset: te(
      "Strike Offset — signed distance from spot. Negative = below spot, positive = above spot.",
    ),
    longOffset: te(
      "Long Offset — position of the bought leg relative to spot or the strategy center.",
    ),
    shortOffset: te(
      "Short Offset — position of the sold leg relative to spot or the paired long leg.",
    ),
    callOffset: te(
      "Call Offset — distance from spot to the call strike. Higher = farther above spot.",
    ),
    putOffset: te(
      "Put Offset — distance from spot to the put strike. Higher = farther below spot.",
    ),
    width: te(
      "Wing Width — distance from the center/body to the outer wings. Wider = more potential profit, but weaker protection or higher cost.",
    ),
    outerWidth: te(
      "Wing Width — distance from the center/body to the outer wings. Wider = more potential profit, but weaker protection or higher cost.",
    ),
    innerLow: te(
      "Lower Body — distance from the center to the lower inner strike.",
    ),
    innerHigh: te(
      "Upper Body — distance from the center to the upper inner strike.",
    ),
    skew: te(
      "Skew — shifts the center strike away from at-the-money. Negative = lower, positive = higher.",
    ),
    putSkew: te(
      "Put Skew — shifts the put-side back-month strike. Negative = lower, positive = higher.",
    ),
    callSkew: te(
      "Call Skew — shifts the call-side back-month strike. Negative = lower, positive = higher.",
    ),
    putShortOffset: te(
      "Put Short — distance below spot for the short put strike.",
    ),
    putLongOffset: te(
      "Put Long — distance below spot for the protective long put strike.",
    ),
    callShortOffset: te(
      "Call Short — distance above spot for the short call strike.",
    ),
    callLongOffset: te(
      "Call Long — distance above spot for the protective long call strike.",
    ),
    s1: te("Short 1 Offset — distance from the center to the first short leg."),
    s2: te(
      "Short 2 Offset — distance from the center to the second short leg.",
    ),
    l2: te("Long Wing Offset — distance from the center to the long wing."),
    short1Offset: te(
      "Short 1 Offset — position of the first sold leg relative to the strategy center.",
    ),
    short2Offset: te(
      "Short 2 Offset — position of the second sold leg relative to the strategy center.",
    ),
    long1Offset: te(
      "Long 1 Offset — position of the first bought leg relative to the strategy center.",
    ),
    long2Offset: te(
      "Long 2 Offset — position of the second bought leg relative to the strategy center.",
    ),
    nakedOffset: te(
      "Naked Leg Offset — distance from spot to the uncovered sold option leg.",
    ),
    spreadShortOffset: te(
      "Spread Short Offset — distance from spot to the short leg of the spread.",
    ),
    spreadLongOffset: te(
      "Spread Long Offset — distance from spot to the protective long leg of the spread.",
    ),
    frontDTE: te("Front DTE — days to expiration for the near-term leg."),
    backDTE: te("Back DTE — days to expiration for the later-term leg."),
  };

  if (!layer) return helps;

  if (
    kind === "single_call" ||
    kind === "single_put" ||
    kind === "synthetic_future"
  ) {
    helps.strikeOffset = te(
      "Strike Offset — current side: {side}. Negative = below spot, positive = above spot.",
      { side: longShort },
    );
  }

  if (kind === "iron_fly") {
    helps.width = isLongSide
      ? te(
          "Wing Width — current side: Long iron fly. Outer wings are sold; wider wings give large moves more room but usually change the debit paid.",
        )
      : te(
          "Wing Width — current side: Short iron fly. Outer wings are bought for protection; wider wings increase possible credit but weaken protection.",
        );
    helps.skew = te(
      "Skew — current side: {side}. Negative shifts the iron fly center lower; positive shifts it higher.",
      { side: longShort },
    );
  }

  if (kind === "butterfly") {
    helps.width = isLongSide
      ? te(
          "Wing Width — current side: Long butterfly. Wider wings broaden the target zone but usually increase cost.",
        )
      : te(
          "Wing Width — current side: Short butterfly. Wider wings broaden the risk zone and change the credit received.",
        );
  }

  if (kind === "iron_condor") {
    helps.putShortOffset = te(
      "Put Short — current side: {side}. This inner put leg is currently {legSide}.",
      { side: longShort, legSide: longShort },
    );
    helps.callShortOffset = te(
      "Call Short — current side: {side}. This inner call leg is currently {legSide}.",
      { side: longShort, legSide: longShort },
    );
    helps.putLongOffset = te(
      "Put Long — current side: {side}. This outer put leg is currently {legSide}.",
      { side: longShort, legSide: shortLong },
    );
    helps.callLongOffset = te(
      "Call Long — current side: {side}. This outer call leg is currently {legSide}.",
      { side: longShort, legSide: shortLong },
    );
  }

  if (kind === "broken_wing_butterfly") {
    helps.s1 = te(
      "Short 1 Offset — current direction: {direction}. Moves the first short leg toward that side.",
      { direction },
    );
    helps.s2 = te(
      "Short 2 Offset — current direction: {direction}. Moves the second short leg farther on that side.",
      { direction },
    );
    helps.l2 = te(
      "Long Wing Offset — current direction: {direction}. Extends the wider protective wing on that side.",
      { direction },
    );
  }

  if (kind === "jade_lizard") {
    helps.nakedOffset = te(
      "Naked Leg Offset — current direction: {direction}. Controls the uncovered short option leg.",
      { direction },
    );
    helps.spreadShortOffset = te(
      "Spread Short Offset — current direction: {direction}. Controls the short leg of the defined-risk spread.",
      { direction },
    );
    helps.spreadLongOffset = te(
      "Spread Long Offset — current direction: {direction}. Controls the protective long leg of the spread.",
      { direction },
    );
  }

  if (
    kind === "ratio_backspread" ||
    kind === "ratio_spread" ||
    kind === "time_spread"
  ) {
    helps.longOffset = te(
      "Long Offset — current type: {optionType}. Controls the bought {optionType} leg.",
      { optionType },
    );
    helps.shortOffset = te(
      "Short Offset — current type: {optionType}. Controls the sold {optionType} leg.",
      { optionType },
    );
    helps.strikeOffset = te(
      "Strike Offset — current type: {optionType}. Negative = below spot, positive = above spot.",
      { optionType },
    );
    helps.skew = te(
      "Skew — current type: {optionType}. Positive moves the back-month leg toward at-the-money; negative moves it away.",
      { optionType },
    );
  }

  if (kind === "strangle" || kind === "combo" || kind === "guts") {
    helps.callOffset = te(
      "Call Offset — current side: {side}. Controls the {side} call leg distance from spot.",
      { side: longShort },
    );
    helps.putOffset = te(
      "Put Offset — current side: {side}. Controls the {side} put leg distance from spot.",
      { side: longShort },
    );
  }

  if (kind === "double_time_spread") {
    helps.putSkew = te(
      "Put Skew — positive moves the back-month put toward at-the-money; negative moves it away.",
    );
    helps.callSkew = te(
      "Call Skew — positive moves the back-month call toward at-the-money; negative moves it away.",
    );
  }

  return helps;
}

export function useEventDescriptions(): Record<string, string> {
  const te = useExtracted();
  return {
    CPI: te("Consumer Price Index"),
    NFP: te("Nonfarm Payrolls"),
    FOMC: te("Federal Reserve Rate Decision"),
    PPI: te("Producer Price Index"),
    "Jobless Claims": te("Weekly Unemployment Claims"),
  };
}

/** Manifold X/Y/Z axis labels — sweep inputs, engine outputs, and the P&L
 *  colormap label. Greek glyphs (Θ Δ Γ ν) are not translated and live in
 *  `OUTPUT_GREEKS` in `lib/manifold/axes.ts`. */
export function useAxisLabels(): {
  sweep: Record<SweepField, string>;
  output: Record<OutputField, string>;
  pnl: string;
} {
  const te = useExtracted();
  const PRICE_LABEL = te("Spot (S)");
  const STRIKE_LABEL = te("Strike (K)");
  const MONEYNESS_LABEL = te("Moneyness (m)");
  const IV_LABEL = te("IV (σ)");
  const DTE_LABEL = te("DTE (T)");
  const PREMIUM_LABEL = te("Premium (V)");
  const THETA_LABEL = te("Theta");
  const DELTA_LABEL = te("Delta");
  const GAMMA_LABEL = te("Gamma");
  const VEGA_LABEL = te("Vega");
  const PNL_LABEL = te("P&L");
  return {
    sweep: {
      price: PRICE_LABEL,
      strike: STRIKE_LABEL,
      moneyness: MONEYNESS_LABEL,
      iv: IV_LABEL,
      dte: DTE_LABEL,
    },
    output: {
      pnl: PNL_LABEL,
      premium: PREMIUM_LABEL,
      theta: THETA_LABEL,
      delta: DELTA_LABEL,
      gamma: GAMMA_LABEL,
      vega: VEGA_LABEL,
    },
    pnl: PNL_LABEL,
  };
}

export function useHolidayLabels(): Record<string, string> {
  const te = useExtracted();
  return {
    "New Year's Day": te("New Year's Day"),
    "New Year's Day (observed)": te("New Year's Day (observed)"),
    "Martin Luther King Jr. Day": te("Martin Luther King Jr. Day"),
    "Presidents' Day": te("Presidents' Day"),
    "Good Friday": te("Good Friday"),
    "Memorial Day": te("Memorial Day"),
    Juneteenth: te("Juneteenth"),
    "Juneteenth (observed)": te("Juneteenth (observed)"),
    "Independence Day": te("Independence Day"),
    "Independence Day (observed)": te("Independence Day (observed)"),
    "Labor Day": te("Labor Day"),
    Thanksgiving: te("Thanksgiving"),
    Christmas: te("Christmas"),
    "Christmas (observed)": te("Christmas (observed)"),
  };
}
